import type { Address } from "viem"
import type { Config } from "wagmi"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import { EventEmitter, makeGroupId } from "@/domains/library"
import { LockResetEvent } from "@/domains/lock/events"

import type { IResetNftActions } from "./interfaces"

interface ResetNftInteractionParams {
  account: Address
  chainId: number
  config: Config
  voterAddress: Address
  tokenId: number
  emitter: EventEmitter<EmitEvent> | undefined
  eventBus: IEventBus
  onChainActions: IResetNftActions
}

export const resetNftInteraction = async ({
  account,
  chainId,
  config,
  voterAddress,
  tokenId,
  emitter,
  eventBus,
  onChainActions,
}: ResetNftInteractionParams) => {
  // Generate a unique group ID for this interaction instance
  const groupId = makeGroupId("reset-nft")

  const result = await onChainActions.reset({
    account,
    chainId,
    config,
    voterAddress,
    tokenId,
  })

  if (result.success) {
    eventBus.publish(new LockResetEvent(groupId, tokenId, result.tx))
    if (emitter) {
      emitter.emit("reset-nft", {
        title: "Reset NFT",
        description: `NFT #${tokenId} reset`,
        variant: "success",
      })
    }
    return result
  }

  if (result.success === false) {
    if (emitter) {
      if (typeof result.error === "string" && result.error.includes("Distribute gauge first")) {
        emitter.emit("error-resetting-nft", {
          title: "Reset NFT failed",
          description: "Gauge not distributed, please contact support in our discord",
          variant: "error",
        })
        return result
      }

      emitter.emit("error-resetting-nft", {
        title: "Reset NFT failed",
        description: `Unable to reset NFT`,
        variant: "error",
      })
    }
    return false
  }

  return result
}
